import React from 'react';
import { Users, CreditCard, Phone, GitBranch, TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import {
  useTenantAdoptionSummary,
  useTenantAdoptionToday,
  useCardMetricsSummary,
  useCardMetricsToday,
  useDailyCallCenterHealthSummary,
} from '../hooks/useReports';
import { useExecutiveDashboard } from '../hooks/useJira';
import { formatNumber, formatCompactNumber, formatChartDate } from '../utils/formatters';

/* ───────── tiny helpers ───────── */

function KPI({ label, value, change, invertChange, loading }) {
  const formatted = loading ? '–' : typeof value === 'string' ? value : formatCompactNumber(value ?? 0);
  const trend = change === undefined || change === null;
  const positive = invertChange ? change < 0 : change > 0;
  const negative = invertChange ? change > 0 : change < 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
      <p className="text-xs sm:text-sm font-medium text-gray-500">{label}</p>
      <p className={`mt-1 text-xl sm:text-2xl font-bold text-gray-900 ${loading ? 'animate-pulse' : ''}`}>{formatted}</p>
      {!trend && (
        <span className={`inline-flex items-center gap-1 mt-2 text-xs font-medium ${positive ? 'text-green-600' : negative ? 'text-red-600' : 'text-gray-500'}`}>
          {positive ? <TrendingUp className="w-3.5 h-3.5" /> : negative ? <TrendingDown className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
          {Math.abs(change).toFixed(1)}% vs yesterday
        </span>
      )}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, color }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
  };
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className={`p-2 rounded-lg ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
  );
}

function HealthDot({ status }) {
  const c = status === 'green' ? 'bg-green-500' : status === 'amber' ? 'bg-amber-500' : 'bg-red-500';
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${c}`} />;
}

/* ───────── main component ───────── */

export default function Reports() {
  // Member Growth
  const { data: adoptionSummary, isLoading: adoptionLoading } = useTenantAdoptionSummary();
  const { data: adoptionToday, isLoading: todayAdoptionLoading } = useTenantAdoptionToday();

  // Card Operations
  const { data: cardSummary, isLoading: cardLoading } = useCardMetricsSummary();
  const { data: cardToday, isLoading: cardTodayLoading } = useCardMetricsToday();

  // Call Center
  const { data: callHealth, isLoading: callLoading } = useDailyCallCenterHealthSummary();

  // Technology
  const { data: techData, isLoading: techLoading } = useExecutiveDashboard();

  /* ── derived data ── */
  const adoption = adoptionSummary?.data?.[0] || {};
  const todayAdoption = adoptionToday?.data || [];
  const todayOnboarded = todayAdoption.reduce((s, t) => s + (t.new_adopted_consumers || 0), 0);
  const todayLogins = todayAdoption.reduce((s, t) => s + (t.count_logins || 0), 0);

  const card = cardSummary?.data?.[0] || {};
  const todayCard = cardToday?.data || [];
  const todayOrdered = todayCard.reduce((s, t) => s + (t.cards_ordered_count || 0), 0);
  const todayShipped = todayCard.reduce((s, t) => s + (t.cards_shipped_count || 0), 0);
  const todayActivated = todayCard.reduce((s, t) => s + (t.cards_activated_count || 0), 0);

  const callDays = callHealth?.data || [];
  const today = callDays[0] || {};
  const yesterday = callDays[1] || {};
  const pctChange = (curr, prev) => prev ? ((curr - prev) / prev) * 100 : 0;

  // 14-day chart data (reverse so oldest is first) - dates in EST
  const last14 = callDays.slice(0, 14).reverse();
  const chartData = last14.map((d) => ({
    date: formatChartDate(d.call_date),
    volume: d.total_interactions,
    sla: d.sla_30s_pct,
  }));

  // Technology
  const tech = techData?.data || {};
  const sprintTeams = tech.sprintTeams || [];
  const kanbanTeams = tech.kanbanTeams || [];
  const allTeams = [...sprintTeams, ...kanbanTeams].filter((t) => !t.error);

  const combinedVelocity = sprintTeams.filter((t) => !t.error).reduce((s, t) => s + (t.avgVelocity || 0), 0);
  const avgCompletion = sprintTeams.filter((t) => !t.error).length
    ? sprintTeams.filter((t) => !t.error).reduce((s, t) => s + (100 - (t.avgSpillover || 0)), 0) / sprintTeams.filter((t) => !t.error).length
    : 0;
  const totalBugs = kanbanTeams.filter((t) => !t.error).reduce((s, t) => s + (t.openBugCount || 0), 0);
  const avgTurnaround = kanbanTeams.filter((t) => !t.error).length
    ? kanbanTeams.filter((t) => !t.error).reduce((s, t) => s + (t.avgTurnaroundDays || 0), 0) / kanbanTeams.filter((t) => !t.error).length
    : 0;

  const teamHealth = (team) => {
    if (team.avgSpillover !== undefined) {
      return team.avgSpillover <= 10 ? 'green' : team.avgSpillover <= 25 ? 'amber' : 'red';
    }
    return team.openBugCount <= 5 ? 'green' : team.openBugCount <= 15 ? 'amber' : 'red';
  };

  const teamLabel = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Executive Summary</h2>
        <p className="text-gray-500 mt-1 text-sm">Holistic view across Member Growth, Card Operations, Call Center & Technology</p>
      </div>

      {/* ── Section 1: Member Growth ── */}
      <section>
        <SectionHeader icon={Users} title="Member Growth" color="blue" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <KPI label="Total Onboarded" value={adoption.total_members_onboarded} loading={adoptionLoading} />
          <KPI label="Total Logged In" value={adoption.total_members_logged_in} loading={adoptionLoading} />
          <KPI label="Active Clients" value={adoption.total_tenants} loading={adoptionLoading} />
        </div>
        {!todayAdoptionLoading && (
          <p className="mt-3 text-sm text-blue-600 font-medium">
            Today: {formatNumber(todayOnboarded)} new onboarded &middot; {formatNumber(todayLogins)} logins
          </p>
        )}
      </section>

      {/* ── Section 2: Card Operations ── */}
      <section>
        <SectionHeader icon={CreditCard} title="Card Operations" color="amber" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <KPI label="Cards Ordered" value={card.total_cards_ordered} loading={cardLoading} />
          <KPI label="Cards Shipped" value={card.total_cards_shipped} loading={cardLoading} />
          <KPI label="Cards Activated" value={card.total_cards_activated} loading={cardLoading} />
        </div>
        {!cardTodayLoading && (
          <p className="mt-3 text-sm text-amber-600 font-medium">
            Today: {formatNumber(todayOrdered)} ordered &middot; {formatNumber(todayShipped)} shipped &middot; {formatNumber(todayActivated)} activated
          </p>
        )}
      </section>

      {/* ── Section 3: Call Center Health ── */}
      <section>
        <SectionHeader icon={Phone} title="Call Center Health" color="green" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI label="Total Interactions" value={today.total_interactions} change={pctChange(today.total_interactions, yesterday.total_interactions)} loading={callLoading} />
          <KPI label="Answered" value={today.answered_calls} change={pctChange(today.answered_calls, yesterday.answered_calls)} loading={callLoading} />
          <KPI label="Abandoned" value={today.abandoned_calls} change={pctChange(today.abandoned_calls, yesterday.abandoned_calls)} invertChange loading={callLoading} />
          <KPI label="SLA 30s %" value={today.sla_30s_pct != null ? `${Number(today.sla_30s_pct).toFixed(1)}%` : undefined} change={pctChange(today.sla_30s_pct, yesterday.sla_30s_pct)} loading={callLoading} />
        </div>

        {/* Sparkline charts */}
        {chartData.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 mb-3">14-Day Call Volume</p>
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis hide />
                  <Tooltip />
                  <Area type="monotone" dataKey="volume" stroke="#3b82f6" fill="url(#volGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 mb-3">14-Day SLA Trend</p>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={chartData}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis hide domain={[0, 100]} />
                  <Tooltip />
                  <ReferenceLine y={80} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: '80%', fontSize: 10, fill: '#f59e0b' }} />
                  <Line type="monotone" dataKey="sla" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </section>

      {/* ── Section 4: Technology Delivery ── */}
      <section>
        <SectionHeader icon={GitBranch} title="Technology Delivery" color="purple" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI label="Combined Velocity" value={combinedVelocity} loading={techLoading} />
          <KPI label="Completion Rate" value={`${avgCompletion.toFixed(1)}%`} loading={techLoading} />
          <KPI label="Open Bugs" value={totalBugs} loading={techLoading} />
          <KPI label="Avg Turnaround" value={`${avgTurnaround.toFixed(1)}d`} loading={techLoading} />
        </div>

        {/* Team health scorecard */}
        {allTeams.length > 0 && (
          <div className="mt-4 bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-4 py-2">Team</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2 text-center">Health</th>
                  <th className="px-4 py-2 text-right">Key Metric</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {allTeams.map((t) => {
                  const isSprint = t.avgVelocity !== undefined;
                  return (
                    <tr key={t.teamKey} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-900">{teamLabel(t.teamKey)}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${isSprint ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                          {isSprint ? 'Sprint' : 'Kanban'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center"><HealthDot status={teamHealth(t)} /></td>
                      <td className="px-4 py-2 text-right text-gray-600">
                        {isSprint ? `Velocity ${t.avgVelocity}` : `${t.openBugCount} bugs`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
